import { Tag as TagDTO } from '@dto/tag.dto';
import { useCallback, useMemo, useRef, useState } from 'react';
import { FILTER_TYPE_LABELS } from '../constants';
import { createFilterTag } from '../domain/filterFactory';
import { parseSearchQueryString, serializeSearchQuery } from '../domain/queryCodec';
import type {
  FilterTag,
  FilterTagType,
  SelectionType,
  SubsystemClassificationItem,
} from '../search.types';

type FocusTarget = 'editor' | 'remove';
type TechnologyType = 'class' | 'hw_type' | null;

export interface SearchComposerDraft {
  type: SelectionType | null;
  text: string;
  editingIndex: number | null;
  selectedTag: string | null;
  selectedTagCategory: string | null;
  selectedCategoryTag: string | null;
  selectedTechnology: string | null;
  selectedSubsystem: string | null;
  selectedHealthStatus: string | null;
  technologyType: TechnologyType;
}

export interface SearchComposerDropdowns {
  dropdown: boolean;
  tagSelect: boolean;
  tagCategorySelect: boolean;
  categoryTagSelect: boolean;
  technologySelect: boolean;
  subsystemSelect: boolean;
  healthStatusSelect: boolean;
}

interface SearchComposerState {
  filters: FilterTag[];
  freeText: string;
  draft: SearchComposerDraft;
  dropdowns: SearchComposerDropdowns;
  focusedBadgeIndex: number | null;
  focusTarget: FocusTarget;
}

interface SearchSubmissionSnapshot {
  filters: FilterTag[];
  freeText: string;
  queryString: string;
}

interface UseSearchComposerProps {
  availableArchitecture?: SubsystemClassificationItem[];
  availableTagsCategories?: TagDTO[];
}

const TEXT_FILTER_SELECTIONS = new Set<SelectionType>([
  'search',
  'regex',
  'ip',
  'mac',
  'wwn',
  'volid',
]);

const INITIAL_DRAFT: SearchComposerDraft = {
  type: null,
  text: '',
  editingIndex: null,
  selectedTag: null,
  selectedTagCategory: null,
  selectedCategoryTag: null,
  selectedTechnology: null,
  selectedSubsystem: null,
  selectedHealthStatus: null,
  technologyType: null,
};

const INITIAL_DROPDOWNS: SearchComposerDropdowns = {
  dropdown: false,
  tagSelect: false,
  tagCategorySelect: false,
  categoryTagSelect: false,
  technologySelect: false,
  subsystemSelect: false,
  healthStatusSelect: false,
};

const INITIAL_STATE: SearchComposerState = {
  filters: [],
  freeText: '',
  draft: INITIAL_DRAFT,
  dropdowns: INITIAL_DROPDOWNS,
  focusedBadgeIndex: null,
  focusTarget: 'editor',
};

const createInitialDraft = (): SearchComposerDraft => ({ ...INITIAL_DRAFT });

const createInitialDropdowns = (): SearchComposerDropdowns => ({ ...INITIAL_DROPDOWNS });

const matchesFilterType = (filter: FilterTag, nextType: FilterTagType): boolean => {
  if (nextType === 'class_subsystem' || nextType === 'hw_type_subsystem') {
    return filter.type === 'class_subsystem' || filter.type === 'hw_type_subsystem';
  }
  if (nextType === 'tag_category_tag') {
    return filter.type === 'tag_category_tag';
  }
  return filter.type === nextType;
};

const hasValidDraftValue = (draft: SearchComposerDraft): boolean => {
  if (!draft.type) return false;

  if (TEXT_FILTER_SELECTIONS.has(draft.type)) {
    return draft.text.trim().length > 0;
  }
  if (draft.type === 'tag') {
    return Boolean(draft.selectedTag);
  }
  if (draft.type === 'tag_category') {
    return Boolean(draft.selectedTagCategory && draft.selectedCategoryTag);
  }
  if (draft.type === 'technology') {
    return Boolean(draft.selectedTechnology && draft.selectedSubsystem && draft.technologyType);
  }
  if (draft.type === 'health_status') {
    return Boolean(draft.selectedHealthStatus);
  }

  return false;
};

const buildDraftFromFilter = (
  selectionType: SelectionType,
  existingFilter: FilterTag | undefined,
  editingIndex: number | undefined
): SearchComposerDraft => {
  const nextDraft = createInitialDraft();
  nextDraft.type = selectionType;
  nextDraft.editingIndex = editingIndex ?? null;

  if (!existingFilter) {
    return nextDraft;
  }

  switch (selectionType) {
    case 'tag':
      nextDraft.selectedTag = existingFilter.value.trim();
      break;
    case 'tag_category': {
      const [categoryValue, tagValue] = existingFilter.value.split(':');
      nextDraft.selectedTagCategory = categoryValue || null;
      nextDraft.selectedCategoryTag = tagValue || null;
      break;
    }
    case 'technology': {
      const [technologyValue, subsystemValue] = existingFilter.value.split(':');
      nextDraft.selectedTechnology = technologyValue || null;
      nextDraft.selectedSubsystem = subsystemValue || null;
      nextDraft.technologyType = existingFilter.type === 'class_subsystem' ? 'class' : 'hw_type';
      break;
    }
    case 'health_status':
      nextDraft.selectedHealthStatus = existingFilter.value || null;
      break;
    default:
      nextDraft.text = existingFilter.value;
      break;
  }

  return nextDraft;
};

const buildFilterFromDraft = (
  draft: SearchComposerDraft,
  availableArchitecture: SubsystemClassificationItem[],
  availableTagsCategories: TagDTO[]
): FilterTag | null => {
  if (!draft.type) return null;

  let filterType: FilterTagType;
  let filterValue: string | null = null;

  switch (draft.type) {
    case 'tag':
      filterType = 'tag';
      filterValue = draft.selectedTag;
      break;
    case 'tag_category':
      filterType = 'tag_category_tag';
      filterValue =
        draft.selectedTagCategory && draft.selectedCategoryTag
          ? `${draft.selectedTagCategory}:${draft.selectedCategoryTag}`
          : null;
      break;
    case 'technology':
      filterType = draft.technologyType === 'class' ? 'class_subsystem' : 'hw_type_subsystem';
      filterValue =
        draft.selectedTechnology && draft.selectedSubsystem
          ? `${draft.selectedTechnology}:${draft.selectedSubsystem}`
          : null;
      break;
    case 'health_status':
      filterType = 'health_status';
      filterValue = draft.selectedHealthStatus;
      break;
    default:
      filterType = draft.type;
      filterValue = draft.text.trim();
      break;
  }

  if (!filterValue) return null;

  return createFilterTag(filterType, filterValue, {
    availableArchitecture,
    availableTagsCategories,
    typeLabels: FILTER_TYPE_LABELS,
  });
};

const updateFilters = (
  filters: FilterTag[],
  nextFilter: FilterTag,
  editingIndex: number | null
): FilterTag[] => {
  if (editingIndex !== null && editingIndex >= 0 && editingIndex < filters.length) {
    return filters.map((filter, index) => (index === editingIndex ? nextFilter : filter));
  }

  const existingIndex = filters.findIndex((filter) => matchesFilterType(filter, nextFilter.type));
  if (existingIndex >= 0) {
    return filters.map((filter, index) => (index === existingIndex ? nextFilter : filter));
  }

  return [...filters, nextFilter];
};

export const useSearchComposer = ({
  availableArchitecture = [],
  availableTagsCategories = [],
}: UseSearchComposerProps) => {
  const [state, setState] = useState<SearchComposerState>(INITIAL_STATE);
  const stateRef = useRef<SearchComposerState>(INITIAL_STATE);

  const commitResolvedState = useCallback((updater: (currentState: SearchComposerState) => SearchComposerState) => {
    const nextState = updater(stateRef.current);
    stateRef.current = nextState;
    setState(nextState);
    return nextState;
  }, []);

  const commitDraftState = useCallback((currentState: SearchComposerState, patch?: Partial<SearchComposerDraft>): SearchComposerState => {
    const nextDraft = { ...currentState.draft, ...patch };
    let nextFilters = currentState.filters;

    if (nextDraft.type) {
      const nextFilter = buildFilterFromDraft(
        nextDraft,
        availableArchitecture,
        availableTagsCategories
      );

      if (nextFilter) {
        nextFilters = updateFilters(currentState.filters, nextFilter, nextDraft.editingIndex);
      } else if (nextDraft.editingIndex !== null) {
        nextFilters = currentState.filters.filter((_, index) => index !== nextDraft.editingIndex);
      }
    }

    return {
      ...currentState,
      filters: nextFilters,
      draft: createInitialDraft(),
      dropdowns: createInitialDropdowns(),
      focusedBadgeIndex: null,
      focusTarget: 'editor',
    };
  }, [availableArchitecture, availableTagsCategories]);

  const resetComposer = useCallback(() => {
    commitResolvedState(() => INITIAL_STATE);
  }, [commitResolvedState]);

  const setFreeText = useCallback((value: string) => {
    commitResolvedState((currentState) => ({
      ...currentState,
      freeText: value,
    }));
  }, [commitResolvedState]);

  const startDraft = useCallback((
    selectionType: SelectionType,
    existingFilter?: FilterTag,
    editingIndex?: number
  ) => {
    commitResolvedState((currentState) => {
      const nextDraft = buildDraftFromFilter(selectionType, existingFilter, editingIndex);
      const nextDropdowns = createInitialDropdowns();

      if (selectionType === 'tag') {
        nextDropdowns.tagSelect = true;
      } else if (selectionType === 'tag_category') {
        nextDropdowns.tagCategorySelect = true;
      } else if (selectionType === 'technology') {
        nextDropdowns.technologySelect = true;
      } else if (selectionType === 'health_status') {
        nextDropdowns.healthStatusSelect = true;
      }

      return {
        ...currentState,
        freeText: '',
        draft: nextDraft,
        dropdowns: nextDropdowns,
        focusedBadgeIndex: null,
        focusTarget: 'editor',
      };
    });
  }, [commitResolvedState]);

  const updateDraft = useCallback((patch: Partial<SearchComposerDraft>) => {
    commitResolvedState((currentState) => ({
      ...currentState,
      draft: {
        ...currentState.draft,
        ...patch,
      },
    }));
  }, [commitResolvedState]);

  const cancelDraft = useCallback(() => {
    commitResolvedState((currentState) => ({
      ...currentState,
      draft: createInitialDraft(),
      dropdowns: createInitialDropdowns(),
    }));
  }, [commitResolvedState]);

  const setDropdown = useCallback((field: keyof SearchComposerDropdowns, value: boolean) => {
    commitResolvedState((currentState) => ({
      ...currentState,
      dropdowns: {
        ...currentState.dropdowns,
        [field]: value,
      },
    }));
  }, [commitResolvedState]);

  const removeFilter = useCallback((index: number): FilterTag[] => {
    const nextState = commitResolvedState((currentState) => {
      const nextFilters = currentState.filters.filter((_, filterIndex) => filterIndex !== index);
      const nextDraft = { ...currentState.draft };

      if (nextDraft.editingIndex !== null) {
        if (nextDraft.editingIndex === index) {
          return {
            ...currentState,
            filters: nextFilters,
            draft: createInitialDraft(),
            dropdowns: createInitialDropdowns(),
          };
        }
        if (nextDraft.editingIndex > index) {
          nextDraft.editingIndex -= 1;
        }
      }

      return {
        ...currentState,
        filters: nextFilters,
        draft: nextDraft,
      };
    });

    return nextState.filters;
  }, [commitResolvedState]);

  const replaceFilter = useCallback((index: number, updatedFilter: FilterTag): FilterTag[] => {
    const nextState = commitResolvedState((currentState) => ({
      ...currentState,
      filters: currentState.filters.map((filter, filterIndex) =>
        filterIndex === index ? updatedFilter : filter
      ),
    }));

    return nextState.filters;
  }, [commitResolvedState]);

  const clearAll = useCallback(() => {
    commitResolvedState((currentState) => ({
      ...currentState,
      filters: [],
      draft: createInitialDraft(),
      focusedBadgeIndex: null,
      focusTarget: 'editor',
    }));
  }, [commitResolvedState]);

  const promoteFreeTextToSearchFilter = useCallback(() => {
    commitResolvedState((currentState) => {
      const filterValue = currentState.freeText.trim();
      if (!filterValue) return currentState;

      const searchFilter = createFilterTag('search', filterValue, {
        availableArchitecture,
        availableTagsCategories,
        typeLabels: FILTER_TYPE_LABELS,
      });

      return {
        ...currentState,
        filters: updateFilters(currentState.filters, searchFilter, null),
        freeText: '',
        draft: createInitialDraft(),
        dropdowns: createInitialDropdowns(),
      };
    });
  }, [availableArchitecture, availableTagsCategories, commitResolvedState]);

  const commitDraft = useCallback((patch?: Partial<SearchComposerDraft>): FilterTag[] => {
    const nextState = commitResolvedState((currentState) => commitDraftState(currentState, patch));
    return nextState.filters;
  }, [commitDraftState, commitResolvedState]);

  const loadFromQuery = useCallback((query: string) => {
    const { filters, searchValue } = parseSearchQueryString(query, availableArchitecture);

    commitResolvedState((currentState) => ({
      ...currentState,
      filters,
      freeText: searchValue,
      draft: createInitialDraft(),
      dropdowns: createInitialDropdowns(),
      focusedBadgeIndex: null,
      focusTarget: 'editor',
    }));
  }, [availableArchitecture, commitResolvedState]);

  const setFocusedBadgeIndex = useCallback((index: number | null) => {
    commitResolvedState((currentState) => ({
      ...currentState,
      focusedBadgeIndex: index,
    }));
  }, [commitResolvedState]);

  const setFocusTarget = useCallback((target: FocusTarget) => {
    commitResolvedState((currentState) => ({
      ...currentState,
      focusTarget: target,
    }));
  }, [commitResolvedState]);

  const buildSubmissionSnapshot = useCallback((): SearchSubmissionSnapshot => {
    const nextState = commitResolvedState((currentState) => commitDraftState(currentState));

    return {
      filters: nextState.filters,
      freeText: nextState.freeText,
      queryString: serializeSearchQuery(nextState.filters, nextState.freeText),
    };
  }, [commitDraftState, commitResolvedState]);

  const isDisabledSearchButton = useMemo(() => {
    return (
      state.filters.length === 0 &&
      state.freeText.trim().length === 0 &&
      !hasValidDraftValue(state.draft)
    );
  }, [state.draft, state.filters, state.freeText]);

  return {
    filters: state.filters,
    freeText: state.freeText,
    draft: state.draft,
    dropdowns: state.dropdowns,
    focusedBadgeIndex: state.focusedBadgeIndex,
    focusTarget: state.focusTarget,
    isDisabledSearchButton,
    startDraft,
    updateDraft,
    commitDraft,
    cancelDraft,
    setFreeText,
    promoteFreeTextToSearchFilter,
    setDropdown,
    removeFilter,
    replaceFilter,
    clearAll,
    resetComposer,
    loadFromQuery,
    buildSubmissionSnapshot,
    setFocusedBadgeIndex,
    setFocusTarget,
  };
};
